import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Container,
    Row,
    Col,
    Button,
} from "reactstrap";
import { process } from "@progress/kendo-data-query";
import { useDispatch, useSelector } from "react-redux";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { ColumnMenu } from "../components/kendoComponents/columnMenu/ColumnMenu";
import { ColumnProps } from "../components/kendoComponents/columnProps/ColumnProps";
import { GridPager } from "../components/kendoComponents/gridPager/GridPager";
import "../assets/scss/kendo-theme-custom.scss";
import "./processRuns.scss";
import FeatherIcon from "feather-icons-react";
import "react-hammerjs";
import LoaderSpin from "../components/loaderSpin/LoaderSpin";
import { fetchRedXRoles, fetchUserList, removeUser, saveUser } from "../store/app/DataQualityAdmin/DataQualityAdminSlice";


const RoleCell = (props) => {
    const { dataItem, editingUserId, setEditingUserId, editingRoleId, setEditingRoleId, redXRoles, dispatch, editingCellRef } = props;
    const isEditing = editingUserId === dataItem.user_name;

    const handleEditClick = () => {
        setEditingUserId(dataItem.user_name);
        setEditingRoleId(dataItem.role_id);
    };

    const handleRoleChange = (e) => {
        const newRoleId = Number(e.target.value);
        setEditingRoleId(newRoleId);
        if (newRoleId !== dataItem.role_id) {
            dispatch(saveUser(
                {
                    userName: dataItem.user_name,
                    roleId: newRoleId,
                    firstName: dataItem.first_name,
                    lastName: dataItem.last_name,
                    companyId: dataItem.company_id || '',
                    isValid: dataItem.is_valid,
                },
                true
            ));
            dispatch(fetchUserList());
        }
        setEditingUserId(null);
        setEditingRoleId(null);
    };

    if (isEditing) {
        return (
            <td ref={editingCellRef}>
                <Input
                    type="select"
                    value={editingRoleId}
                    onChange={handleRoleChange}
                    size="sm"
                    autoFocus
                >
                    {redXRoles.map(role => (
                        <option key={role.roleId} value={role.roleId}>{role.roleName}</option>
                    ))}
                </Input>
            </td>
        );
    }

    return (
        <td style={{ cursor: "pointer" }} onClick={handleEditClick} title="Change role">
            {dataItem.role_name}
            <FeatherIcon icon="edit" size={14} className="ms-2 text-muted" />
        </td>
    );
};

RoleCell.propTypes = {
    dataItem: PropTypes.shape({
        user_name: PropTypes.string.isRequired,
        role_id: PropTypes.number.isRequired,
        role_name: PropTypes.string.isRequired,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        company_id: PropTypes.string,
        is_valid: PropTypes.bool,
    }).isRequired,
    editingUserId: PropTypes.string,
    setEditingUserId: PropTypes.func.isRequired,
    editingRoleId: PropTypes.number,
    setEditingRoleId: PropTypes.func.isRequired,
    redXRoles: PropTypes.arrayOf(PropTypes.shape({
        roleId: PropTypes.number.isRequired,
        roleName: PropTypes.string.isRequired,
    })).isRequired,
    dispatch: PropTypes.func.isRequired,
    editingCellRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ]),
};

const ActionCell = (props) => {
    const { dataItem, dispatch } = props;
        const handleRemove = () => {
            if (!window.confirm(`Remove user "${dataItem.user_name}"?`)) return;

            dispatch(removeUser(dataItem.user_name));
            dispatch(fetchUserList());
        };

        return (
            <td>
                <Button size="sm" onClick={handleRemove} title="Remove user">
                    <FeatherIcon icon="x" color="red" size={18} />
                </Button>
            </td>
        );
    };

    ActionCell.propTypes = {
        dataItem: PropTypes.shape({
            user_name: PropTypes.string.isRequired,
        }).isRequired,
        dispatch: PropTypes.func.isRequired,
    };

const DataQualityAdmin = () => {
    const dispatch = useDispatch();


    const isSpinnerVisible = useSelector(
        (state) => state.dataQualityReducer.loading
    );

    const refreshCount = useSelector(
        (state) => state.dataQualityReducer.refreshCount
    );

    const [dataState, setDataState] = useState({
        sort: [{ field: "userName", dir: "desc" }],
        skip: 0,
        take: 20,
    });

    const [dataResult, setDataResult] = useState([]);

    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [userName, setUserName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyId, setCompanyId] = useState('');


    const editingCellRef = useRef(null);

    const [editingUserId, setEditingUserId] = useState(null);
    const [editingRoleId, setEditingRoleId] = useState(null);

    const userList = useSelector((state) => state.dataQualityAdminReducer.userList);
    const redXRoles = useSelector(
        (state) => state.dataQualityAdminReducer.redXRoles
    );

   const isValidEmail = (email) => {
        if (!email || email.length > 254) return false;
                
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        
        return emailRegex.test(email);
    };

    useEffect(() => {
        dispatch(fetchUserList());
        dispatch(fetchRedXRoles());
    }, [refreshCount]);

    useEffect(() => {
        setDataResult(process(userList, dataState));
    }, [userList]);

    useEffect(() => {
        if (!editingUserId) return;

        const handleClickOutside = (event) => {
            if (
                editingCellRef.current &&
                !editingCellRef.current.contains(event.target)
            ) {
                setEditingUserId(null);
                setEditingRoleId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editingUserId]);


    const renderActionCell = (props) => (<ActionCell {...props} dispatch={dispatch} />);
    



    const renderRoleCell = (props) => (
        <RoleCell
            {...props}
            editingUserId={editingUserId}
            setEditingUserId={setEditingUserId}
            editingRoleId={editingRoleId}
            setEditingRoleId={setEditingRoleId}
            redXRoles={redXRoles}
            dispatch={dispatch}
            editingCellRef={editingCellRef}
        />
    );

    return (
        <>
            <Container fluid className="py-2 px-4  position-relative">
                <Row>
                    <Col>
                        <Button
                            color="primary"
                            size="sm"

                            className="mt-4"
                            onClick={() => setAddUserModalOpen(true)}
                        >
                            Add User
                        </Button>
                    </Col>
                </Row>
                <Row className="row mt-2">
                    <Col>
                        <div className="grid_task_container border">
                            <Grid
                                style={{ height: "100%" }}
                                pageable={true}
                                sortable={true}
                                total={dataResult?.length}
                                data={dataResult}
                                {...dataState}
                                pager={(props) => GridPager(props, userList)}
                                onDataStateChange={(e) => {
                                    setDataState(e.dataState);
                                    setDataResult(process(userList, e.dataState));
                                }}
                            >
                                <Column
                                    {...ColumnProps("user_name", dataState)}
                                    title="Username"
                                    columnMenu={ColumnMenu}
                                />
                                <Column
                                    {...ColumnProps("role_name", dataState)}
                                    title="Role Name"
                                    cell={renderRoleCell}
                                    columnMenu={ColumnMenu}
                                />
                                <Column
                                    {...ColumnProps("first_name", dataState)}
                                    title="First Name"
                                    columnMenu={ColumnMenu}
                                />
                                <Column
                                    {...ColumnProps("last_name", dataState)}
                                    title="Last Name"
                                    columnMenu={ColumnMenu}
                                />
                                <Column
                                    {...ColumnProps("company_id", dataState)}
                                    title="Company ID"
                                    columnMenu={ColumnMenu}
                                />
                                <Column cell={renderActionCell} width="120px" />
                            </Grid>
                        </div>
                    </Col>
                </Row>

                <LoaderSpin isVisible={isSpinnerVisible} />
            </Container>
            <Modal isOpen={addUserModalOpen} toggle={() => setAddUserModalOpen(false)}>
                <ModalHeader toggle={() => setAddUserModalOpen(false)}>Add User</ModalHeader>
                <ModalBody>
                    <Input
                        type="email"
                        placeholder="Type User Email..."
                        value={userEmail}
                        onChange={e => setUserEmail(e.target.value)}
                        invalid={userEmail && !isValidEmail(userEmail)}
                    />
                    {userEmail && !isValidEmail(userEmail) && (
                        <div className="text-danger small mt-1">Please enter a valid email address</div>
                    )}
                    <Input
                        type="select"
                        value={selectedRoleId}
                        onChange={e => setSelectedRoleId(e.target.value)}
                        className="mt-3"
                    >
                        <option value="">Select Role</option>
                        {redXRoles.map(role => (
                            <option key={role.roleId} value={role.roleId}>{role.roleName}</option>
                        ))}
                    </Input>
                    <Input
                        type="text"
                        placeholder="Type First Name..."
                        value={userName}
                        className="mt-3"
                        onChange={e => setUserName(e.target.value)}
                    />
                    <Input
                        type="text"
                        placeholder="Type Last Name..."
                        value={lastName}
                        className="mt-3"
                        onChange={e => setLastName(e.target.value)}
                    />
                    <Input
                        type="text"
                        placeholder="Type Company ID..."
                        value={companyId}
                        className="mt-3"
                        onChange={e => setCompanyId(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={() => {
                            dispatch(saveUser(
                                {
                                    userName: userEmail,
                                    roleId: Number(selectedRoleId),
                                    firstName: userName,
                                    lastName: lastName,
                                    companyId: companyId,
                                    isValid: true,
                                },
                                false
                            ));
                            dispatch(fetchUserList());
                            setAddUserModalOpen(false);
                            setUserEmail('');
                            setSelectedRoleId('');
                            setUserName('');
                            setLastName('');
                            setCompanyId('');
                        }}
                        disabled={!userEmail || !selectedRoleId || !isValidEmail(userEmail)}
                    >
                        OK
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default DataQualityAdmin;
